import { KeyboardBackspace } from "@mui/icons-material";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import "../../assets/styles/plans.css";
import { EProductSection } from "../../commons/interfaces/product";
import { setPlanSelected } from "../../redux/features/planStateSlice";
import { ESubscriptionStatus, getPlanById, getPlans } from "../../utils";
import { Plan, PlanBenefitsPanel } from "../Plan";
import { PlanInfo } from "../PlanInfo";
import { LoadingCard } from "../common/LoadingCard";
import { useMySubscriptionInfoSync } from "../subscriptions/hooks/useMySubscriptionInfo.query";
import { colors } from "../ui/colors";
import { PlanCheckoutLayout } from "./PlanCheckoutLayout";

const SECTION_LABELS = {
  flow_mobile: "Flow Mobile",
  flow_subscription: "Flow Subscription",
};

export const PlanType = {
  FREE_TRIAL: "free_trial",
  STANDARD: "standard",
  CORPORATION: "corporation",
};

const EPlansLayoutView = {
  INFO: "info",
  PLANS: "plans",
};

const PLAN_ORDER = {
  free: 0,
  startup: 1,
  solopro: 2,
  business: 3,
};

const getPreviousPlan = (plans, selectedPlan) => {
  if (!selectedPlan) return null;

  const orderedPlans = [...plans].sort((a, b) => {
    const aOrder = PLAN_ORDER[normalizePlanTitle(a.title)] ?? 999;
    const bOrder = PLAN_ORDER[normalizePlanTitle(b.title)] ?? 999;

    return aOrder - bOrder;
  });

  const selectedIndex = orderedPlans.findIndex((plan) => plan.id === selectedPlan.id);

  if (selectedIndex <= 0) return null;

  return orderedPlans[selectedIndex - 1];
};

const getNewBenefitsComparedToPrevious = (selectedPlan, previousPlan) => {
  if (!selectedPlan) return [];

  if (!previousPlan) return selectedPlan.benefits ?? [];

  const previousBenefitsSet = new Set((previousPlan.benefits ?? []).map((benefit) => normalizeBenefit(benefit)));

  return (selectedPlan.benefits ?? []).filter((benefit) => {
    return !previousBenefitsSet.has(normalizeBenefit(benefit));
  });
};

const normalizePlanTitle = (title = "") => {
  return title.toLowerCase().replace(/\s+/g, "");
};

const normalizeBenefit = (benefit = "") => {
  return benefit.toLowerCase().trim();
};
const ACTIVE_STATUSES = [ESubscriptionStatus.COMPLETED, ESubscriptionStatus.ACTIVE];

export const PlansLayout = ({
  subscriptionId,
  highlightCurrentPlan = true,
  section,
  title = "",
  subtitle = "",
  emptyTitle = "No Plans Available",
  emptyDescription = "There are no upgrade options available at this time. You are currently on the highest tier plan, or all other plans have been filtered out.",
  emptyButtonLabel = "Back to Subscription",
  showPlanInfo = true,
}) => {
  const resolvedSection = subscriptionId ? EProductSection.FLOW_MOBILE : section;

  const { planSelected, openPlanInfo } = useSelector((state) => state.planState);

  const subscriptionInfo = useSelector((state) => state.userState?.subscriptionInfo);

  const subscription = subscriptionInfo?.subscription;

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [cancel, setCancel] = useState(false);

  const hasSubscription = useMemo(() => {
    const hasMain = Boolean(subscription?.id);
    const hasSecondary = (subscriptionInfo?.subscriptions ?? []).some((item) => {
      const currentId = item?.subscription?.id;
      const isFree = item?.subscription?.paypalId === "free_trial";
      return currentId && !isFree && currentId !== subscription?.id;
    });
    return hasMain || hasSecondary;
  }, [subscription, subscriptionInfo]);
  const benefitsRef = useRef(null);

  const [currentView, setCurrentView] = useState(() => {
    const shouldStartOnInfo = hasSubscription && resolvedSection === EProductSection.FLOW_SUBSCRIPTION;

    return shouldStartOnInfo ? EPlansLayoutView.INFO : EPlansLayoutView.PLANS;
  });

  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();

  const { isLoading: loadingSubInfo, error } = useMySubscriptionInfoSync();

  const shouldRenderPlanInfoFirst = (hasSubscription && resolvedSection === EProductSection.FLOW_SUBSCRIPTION) || showPlanInfo;

  const canReturnToPlanInfo = shouldRenderPlanInfoFirst;

  const subscriptions = subscriptionInfo?.subscriptions ?? [];

  const currentSubscriptionById = useMemo(() => {
    if (!subscriptionId) return null;

    return subscriptions.find((item) => item?.subscription?.id === subscriptionId) ?? null;
  }, [subscriptions, subscriptionId]);

  const currentPlanId = useMemo(() => {
    if (!currentSubscriptionById?.subscription?.plan) return null;

    const currentPlan = currentSubscriptionById.subscription.plan;

    if (currentPlan.isFreeTrial || currentPlan.isCustom) {
      return currentPlan.id;
    }

    return currentPlan.paypalPlanId;
  }, [currentSubscriptionById]);

  const fetchPlans = async () => {
    try {
      const availablePlans = await getPlans(resolvedSection);

      const sortedPlans = availablePlans?.sort((a, b) => a?.order - b?.order) ?? [];

      const mappedPlans = [];

      for (const plan of sortedPlans) {
        const { isFreeTrial, isCustom } = plan;
        const planId = plan?.paypalPlanId ? plan?.paypalPlanId : plan?.id;

        const planDetail = await getPlanById(planId, isFreeTrial, isCustom, plan?.billingType);
        const isPayAsYouGo = plan?.billingType === "PAY_AS_YOU_GO" || planDetail?.billingType === "PAY_AS_YOU_GO";

        mappedPlans.push({
          id: planId,
          originalPlanId: plan?.id,
          isCustom: plan?.isCustom,
          isFreeTrial: plan?.isFreeTrial,
          billingType: plan?.billingType || planDetail?.billingType,
          title: planDetail?.product?.name,
          description: planDetail?.product?.description,
          benefits: planDetail?.product?.benefits?.map((benefit) => benefit.name) ?? [],
          price: {
            amount: isPayAsYouGo
              ? (plan?.meta?.basePrice ?? 0)
              : (plan?.meta?.basePrice ??
                planDetail?.billing_cycles?.find((billingCycle) => billingCycle?.tenure_type === "REGULAR")?.pricing_scheme?.fixed_price?.value ??
                0),
            currency: isPayAsYouGo
              ? (plan?.meta?.currency ?? "USD")
              : (planDetail?.billing_cycles?.find((billingCycle) => billingCycle?.tenure_type === "REGULAR")?.pricing_scheme?.fixed_price
                  ?.currency_code ?? "USD"),
            frequency: "monthly",
          },
          hexColor: planDetail?.hexColor,
          isPopular: plan?.isPopular,
          isComingSoon: plan?.isComingSoon,
          trialDetails:
            (planDetail?.product?.name || "").trim().toLowerCase() === "free" || planDetail?.billing_cycles?.length <= 1
              ? null
              : (planDetail?.billing_cycles?.[0] ?? null),
        });
      }

      setPlans(
        mappedPlans.map((plan) => ({
          ...plan,
          isPopular: plan.title?.toLowerCase() === "start up",
        })),
      );
    } catch (fetchError) {
      showBoundary(fetchError);
    }
  };

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoadingPlans(true);
        await fetchPlans();
      } catch (fetchError) {
        console.error(fetchError);
      } finally {
        setLoadingPlans(false);
      }
    };

    loadPlans();
  }, [cancel, resolvedSection]);

  useEffect(() => {
    const nextDefaultView = hasSubscription && resolvedSection === EProductSection.FLOW_SUBSCRIPTION ? EPlansLayoutView.INFO : EPlansLayoutView.PLANS;

    setCurrentView(nextDefaultView);
  }, [hasSubscription, resolvedSection]);

  useEffect(() => {
    if (!openPlanInfo) return;

    setCurrentView(EPlansLayoutView.INFO);
    dispatch(setPlanSelected({ openPlanInfo: false }));
  }, [openPlanInfo, dispatch]);

  useEffect(() => {
    if (error) {
      showBoundary(error);
    }
  }, [error, showBoundary]);
  const plansWithUniqueBenefits = useMemo(() => {
    const activeSubscriptions = subscriptionInfo?.subscriptions?.filter((item) => ACTIVE_STATUSES.includes(item?.subscription?.status)) ?? [];

    const hasActiveFreeTrial = activeSubscriptions.some((item) => item?.subscription?.paypalId === "free_trial");

    const hasActivePaidPlan = activeSubscriptions.some((item) => item?.subscription?.paypalId && item?.subscription?.paypalId !== "free_trial");

    const shouldHideFreeTrial = hasActiveFreeTrial && hasActivePaidPlan;

    return shouldHideFreeTrial ? plans.filter((plan) => !plan.isFreeTrial && plan.id !== "free_trial") : plans;
  }, [plans, subscriptionInfo]);

  const currentComparablePlanId =
    subscription?.status === ESubscriptionStatus.CANCELLED
      ? null
      : subscription?.plan?.isFreeTrial || subscription?.plan?.isCustom
        ? subscription?.plan?.id
        : subscription?.plan?.paypalPlanId;

  const activePlanIds = useMemo(() => {
    return subscriptionId ? [currentPlanId].filter(Boolean) : [currentComparablePlanId].filter(Boolean);
  }, [subscriptionId, currentPlanId, currentComparablePlanId]);

  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (!plansWithUniqueBenefits?.length) return;
    if (selectedPlanId) return;

    const defaultPlan =
      plansWithUniqueBenefits.find((plan) => plan.isPopular && !activePlanIds.includes(plan.id)) ||
      plansWithUniqueBenefits.find((plan) => !activePlanIds.includes(plan.id)) ||
      plansWithUniqueBenefits[0];

    setSelectedPlanId(defaultPlan.id);
  }, [plansWithUniqueBenefits, selectedPlanId, activePlanIds]);

  const selectedPlan = useMemo(() => {
    return plansWithUniqueBenefits.find((plan) => plan.id === selectedPlanId);
  }, [plansWithUniqueBenefits, selectedPlanId]);

  const visiblePlans = plansWithUniqueBenefits;

  const handleShowPlans = () => {
    setCurrentView(EPlansLayoutView.PLANS);
  };

  const handleReturnToPlanInfo = () => {
    if (!canReturnToPlanInfo) return;

    setCurrentView(EPlansLayoutView.INFO);
  };

  const handleGoBack = () => {
    dispatch(setPlanSelected({ upgradeFreeTrial: false }));
    setCancel(true);
    setCurrentView(shouldRenderPlanInfoFirst ? EPlansLayoutView.INFO : EPlansLayoutView.PLANS);
  };

  if (loadingSubInfo || loadingPlans) {
    return <LoadingCard />;
  }

  if (planSelected) {
    return <PlanCheckoutLayout />;
  }

  if (shouldRenderPlanInfoFirst && currentView === EPlansLayoutView.INFO) {
    return <PlanInfo sx={{ margin: "43px 10px 20px -40px" }} setCancel={setCancel} onUpgrade={handleShowPlans} />;
  }

  if (visiblePlans.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          px: 2,
          textAlign: "center",
          maxWidth: 560,
          mx: "auto",
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.5rem",
            color: "var(--fx-surface-text, #0F172A)",
            mb: 1,
          }}
        >
          {emptyTitle}
        </Typography>

        <Typography
          sx={{
            color: "var(--fx-surface-texttertiary, #94A3B8)",
            fontSize: "0.95rem",
            lineHeight: 1.6,
            mb: 2,
          }}
        >
          {emptyDescription}
        </Typography>

        {shouldRenderPlanInfoFirst && (
          <Box>
            <Typography
              onClick={handleGoBack}
              sx={{
                color: "#FB8E00",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {emptyButtonLabel}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  const previousPlan = getPreviousPlan(visiblePlans, selectedPlan);

  const newBenefitsComparedToPrevious = getNewBenefitsComparedToPrevious(selectedPlan, previousPlan);

  const handlePreviewPlan = (planId) => {
    setSelectedPlanId(planId);

    setTimeout(() => {
      benefitsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 2,
        px: 1,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 3, maxWidth: 650 }}>
        {resolvedSection && SECTION_LABELS[resolvedSection] && (
          <Typography
            sx={{
              display: "inline-block",
              fontSize: "0.7rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: "#FB8E00",
              bgcolor: "rgba(251,142,0,0.08)",
              border: "1px solid rgba(251,142,0,0.2)",
              borderRadius: "999px",
              px: 1.5,
              py: 0.4,
              mb: 1.5,
            }}
          >
            {SECTION_LABELS[resolvedSection]}
          </Typography>
        )}

        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.5rem", md: "2rem" },
            color: "var(--fx-surface-text, #0F172A)",
            letterSpacing: "-0.5px",
            lineHeight: 1.2,
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "var(--fx-surface-texttertiary, #94A3B8)",
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </Typography>

        {canReturnToPlanInfo && currentView === EPlansLayoutView.PLANS && hasSubscription && (
          <Button
            onClick={handleReturnToPlanInfo}
            variant="contained"
            endIcon={<KeyboardBackspace />}
            sx={{
              background: colors.orange.base,
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              py: 1.2,
              px: 4,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": { bgcolor: colors.orange[600], boxShadow: "none" },
              borderColor: colors.orange.base,
              fontWeight: 500,
              height: 35,
            }}
          >
            Back to Subscription
          </Button>
        )}
      </Box>

      <Container maxWidth="xl" disableGutters>
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Typography
            sx={{
              fontSize: { xs: "1rem", md: "2.8rem" },
              fontWeight: 900,
              color: colors.gray.base,
              mb: 0.5,
            }}
          >
            Choose the right plan for your workflow
          </Typography>

          <Typography
            sx={{
              fontSize: "1rem",
              color: colors.gray[400],
              maxWidth: 720,
              mx: "auto",
              mb: 8,
              lineHeight: 1.5,
            }}
          >
            Boost your productivity with tools designed to help your business grow from day one.
          </Typography>
        </Box>

        <Grid container spacing={2.5} justifyContent="center" alignItems="stretch" sx={{ mb: 5 }}>
          {visiblePlans.map((plan, index) => {
            const isSelected = selectedPlanId === plan.id;

            return (
              <Grid item key={`plan-grid-${plan.id || index}`} xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "stretch" }}>
                <Plan
                  subscriptionId={subscriptionId}
                  title={plan.title}
                  description={plan.description}
                  benefits={plan.benefits}
                  price={plan.price}
                  bannerHexColor={plan.hexColor}
                  frequency={plan.price?.frequency}
                  planId={plan.id}
                  billingType={plan?.billingType}
                  isCustom={plan?.isCustom}
                  isPopular={plan.isPopular}
                  trialDetails={plan?.trialDetails}
                  isComingSoon={plan.isComingSoon}
                  isCurrentPlan={activePlanIds.includes(plan.id)}
                  isSelected={isSelected}
                  onPreview={() => handlePreviewPlan(plan.id)}
                />
              </Grid>
            );
          })}
        </Grid>

        {selectedPlan && (
          <Box
            ref={benefitsRef}
            key={selectedPlan.id}
            sx={{
              animation: "fadeSlideIn 0.35s ease both",
              "@keyframes fadeSlideIn": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(14px)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <PlanBenefitsPanel
              title={selectedPlan.title}
              description={selectedPlan.description}
              benefits={selectedPlan.benefits}
              price={selectedPlan.price}
              isCurrentPlan={activePlanIds.includes(selectedPlan.id)}
              isFreePlan={selectedPlan.isFreeTrial || selectedPlan.title?.toLowerCase() === "free" || Number(selectedPlan?.price?.amount) === 0}
              previousPlanTitle={previousPlan?.title}
              newBenefits={newBenefitsComparedToPrevious}
              onChoose={() => handlePreviewPlan(selectedPlan.id)}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};
